import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import ModalLayout from '../layouts/ModalLayout'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import { modalPrimaryButtonStyle, modalSecondaryButtonStyle } from '../styles/buttonStyles'
import resetIcon from '../assets/images/icons/reset.png'

const parseReferralSourceChildId = (sourceId) => {
  if (!sourceId) return null
  if (typeof sourceId === 'number') return sourceId

  const s = String(sourceId)
  const id = Number(s.split(':')[0])
  return Number.isFinite(id) ? id : null
}

export default function UserNetworkModal({ open, onClose }) {
  const { t, i18n } = useTranslation()

  const [selectedNodeId] = useState(null)
  const [hoveredNodeId, setHoveredNodeId] = useState(null)
  const [hoveredNode, setHoveredNode] = useState(null)
  const [modalSize, setModalSize] = useState('fullscreen')
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [zoom, setZoom] = useState(1)

const [pan, setPan] = useState({ x: 0, y: 0 })
const [isDragging, setIsDragging] = useState(false)
const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  // layout konštanty z profilu
  const levelGap = 80
  const radius = 16
  const spacingX = 45
  const offset = radius + 20

  const faceByColor = { red: 'sad', gray: 'awkward', green: 'smile' }

  const ymNow = () => {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    return `${y}-${m}`
  }

  const month = ymNow()

  const formatCurrency = (n) => {
    const v = Number(n || 0)
    return `${v.toFixed(2)} €`
  }

  useEffect(() => {
    if (open) setModalSize('fullscreen')
  }, [open])

  const lang = open ? (localStorage.getItem('lang') || 'sk') : 'sk'

  useEffect(() => {
    if (open) i18n.changeLanguage(lang)
  }, [open, lang, i18n])

  const { data: user = null } = useQuery({
    queryKey: ['me', lang],
    enabled: open,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetchWithAuth('/auth/me', {}, lang)
      if (!res.ok) throw new Error('me failed')
      return res.json()
    }
  })

  const { data: networkResponse } = useQuery({
    queryKey: ['network', user?.id],
    enabled: open && !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/referral/network/${user.id}?includeStatus=true`
      )
      if (!res.ok) throw new Error('network failed')
      return res.json()
    }
  })

  const networkData = useMemo(
    () => networkResponse?.network || [],
    [networkResponse]
  )

  const networkStatuses = useMemo(
    () => networkResponse?.statuses || {},
    [networkResponse]
  )

  const NETWORK_LIMIT = 155
  const remaining = Math.max(NETWORK_LIMIT - networkData.length, 0)
  const progressPercent = Math.min(
    (networkData.length / NETWORK_LIMIT) * 100,
    100
  )

  const { data: cbSummary } = useQuery({
    queryKey: ['cashback-summary', user?.id, month],
    enabled: open && !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/cashback/${user.id}?month=${encodeURIComponent(month)}`
      )
      if (!res.ok) return { base: 0, l1: 0, l2: 0, l3: 0, total: 0 }
      return res.json()
    }
  })

  const cbBase = Number(cbSummary?.base) || 0
  const cbL1 = Number(cbSummary?.l1) || 0
  const cbL2 = Number(cbSummary?.l2) || 0
  const cbL3 = Number(cbSummary?.l3) || 0
  const cbTotal = Number(cbSummary?.total) || 0

  const { data: cbByNode = {} } = useQuery({
    queryKey: ['cashback-by-node', user?.id, month],
    enabled: open && !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/cashback/${user.id}/by-node?month=${encodeURIComponent(month)}`
      )
      if (!res.ok) return {}

      const payload = await res.json()
      const map = {}
      ;(payload.items || []).forEach((it) => {
        map[Number(it.child_id)] = {
          amount: Number(it.amount) || 0,
          level: it.level
        }
      })
      return map
    }
  })

  const { data: refBonus } = useQuery({
    queryKey: ['ref-bonus', user?.id, month],
    enabled: open && !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      let res = await fetchWithAuth(
        `/wallet/${user.id}/entries?source=${encodeURIComponent(
          'REFERRAL_BONUS'
        )}&month=${encodeURIComponent(month)}`
      )
      if (!res.ok) res = await fetchWithAuth(`/wallet/${user.id}/entries`)
      if (!res.ok) return { total: 0, byChild: {} }

      const entries = await res.json()

      const normalizeMonth = (row) => {
        if (row?.month) return String(row.month).slice(0, 7)
        if (row?.createdAt) {
          const d = new Date(row.createdAt)
          if (!isNaN(d)) {
            const y = d.getFullYear()
            const m = String(d.getMonth() + 1).padStart(2, '0')
            return `${y}-${m}`
          }
        }
        return null
      }

      const filtered = Array.isArray(entries)
        ? entries.filter((e) => {
            const isRef =
              String(e?.source || e?.type || '').toUpperCase() === 'REFERRAL_BONUS'
            const mm = normalizeMonth(e)
            return isRef && mm === month
          })
        : []

      const totalCents = filtered.reduce(
        (s, e) => s + Math.round(Number(e?.amount || 0) * 100),
        0
      )

      const byChild = {}
      filtered.forEach((e) => {
        const childId = parseReferralSourceChildId(
          e?.source_id || e?.sourceId || e?.child_id
        )
        if (!childId) return
        byChild[childId] = (byChild[childId] || 0) + Number(e?.amount || 0)
      })

      return { total: totalCents / 100, byChild }
    }
  })

  const refBonusTotal = Number(refBonus?.total) || 0
  const refBonusByChild = refBonus?.byChild || {}

  const nodeColors = useMemo(() => {
    const colors = {}

    Object.entries(networkStatuses).forEach(([id, status]) => {
      if (!status.aktivny) {
        colors[id] = 'red'
      } else if (status.hasFuel) {
        colors[id] = 'green'
      } else {
        colors[id] = 'gray'
      }
    })

    return colors
  }, [networkStatuses])

  const levelMap = useMemo(() => {
    const lm = { 1: [], 2: [], 3: [] }
    networkData.forEach(({ generation, child_id }) => {
      if (generation >= 1 && generation <= 3) lm[generation].push(child_id)
    })
    return lm
  }, [networkData])

  const refSumLevel = (lvl) =>
    (levelMap[lvl] || []).reduce((acc, id) => acc + (refBonusByChild[id] || 0), 0)

  const refL1 = refSumLevel(1)
  const refL2 = refSumLevel(2)
  const refL3 = refSumLevel(3)

  const { coords, svgWidth, svgHeight } = useMemo(() => {
    if (!user) return { coords: {}, svgWidth: 0, svgHeight: 0 }

    const treeMap = {}
    networkData.forEach(({ parent_id, child_id }) => {
      if (!treeMap[parent_id]) treeMap[parent_id] = []
      treeMap[parent_id].push(child_id)
    })

    const buildTree = (id) => ({
      id,
      children: (treeMap[id] || []).map(buildTree)
    })
    const root = buildTree(user.id)

    let nextX = 0
    const c = {}

    const dfs = (node, depth) => {
      if (!node.children.length) {
        c[node.id] = { x: nextX * spacingX + offset, y: depth * levelGap + offset }
        nextX++
      } else {
        node.children.forEach((child) => dfs(child, depth + 1))
        const xs = node.children.map((ch) => c[ch.id].x)
        c[node.id] = {
          x: (Math.min(...xs) + Math.max(...xs)) / 2,
          y: depth * levelGap + offset
        }
      }
    }

    dfs(root, 0)

    const allXs = Object.values(c).map((p) => p.x)
    const allYs = Object.values(c).map((p) => p.y)
    const w = allXs.length ? Math.max(...allXs) + offset : 0
    const h = allYs.length ? Math.max(...allYs) + offset : 0

    return { coords: c, svgWidth: w, svgHeight: h }
  }, [user, networkData, offset])

  const renderFace = (type) => {
    const Eyes = (
      <>
        <circle cx={-5} cy={-4} r={1.6} fill="#fff" />
        <circle cx={5} cy={-4} r={1.6} fill="#fff" />
      </>
    )

    if (type === 'smile') {
      return (
        <>
          {Eyes}
          <path
            d="M -6 4 Q 0 8 6 4"
            stroke="#fff"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
        </>
      )
    }

    if (type === 'sad') {
      return (
        <>
          {Eyes}
          <path
            d="M -6 6 Q 0 2 6 6"
            stroke="#fff"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
        </>
      )
    }

    return (
      <>
        {Eyes}
        <line
          x1="-5"
          y1="5"
          x2="5"
          y2="5"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </>
    )
  }

  const renderNodeContent = ({ isHovered, id, faceType }) => {
    if (isHovered) {
      return (
        <text
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="12"
          fill="#fff"
          fontWeight="bold"
          style={{ pointerEvents: 'none' }}
        >
          {id}
        </text>
      )
    }
    return renderFace(faceType)
  }

  if (!open) return null

  return (
    <ModalLayout open={open} onClose={onClose} size={modalSize}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
   	 paddingLeft: 20
        }}
      >
        <button
          onClick={() => setModalSize((prev) => (prev === 'fullscreen' ? 'max' : 'fullscreen'))}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 32
          }}
        >
          ⛶
        </button>

<div
  style={{
    position: 'absolute',
    top: 60,
    right: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  }}
>
<button
  onClick={() => setZoom(z => Math.min(z + 0.2, 3))}
  onMouseEnter={() => setHoverBtn('zoomIn')}
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() => setPressedBtn('zoomIn')}
  onMouseUp={() => setPressedBtn(null)}
  style={modalPrimaryButtonStyle(pressedBtn, 'zoomIn', hoverBtn === 'zoomIn')}
>
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2"/>
    <line x1="11" y1="8" x2="11" y2="14" stroke="currentColor" strokeWidth="2"/>
    <line x1="8" y1="11" x2="14" y2="11" stroke="currentColor" strokeWidth="2"/>
  </svg>
</button>

<button
  onClick={() => setZoom(z => Math.max(z - 0.2, 0.4))}
  onMouseEnter={() => setHoverBtn('zoomOut')}
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() => setPressedBtn('zoomOut')}
  onMouseUp={() => setPressedBtn(null)}
  style={modalPrimaryButtonStyle(pressedBtn, 'zoomOut', hoverBtn === 'zoomOut')}
>
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2"/>
    <line x1="8" y1="11" x2="14" y2="11" stroke="currentColor" strokeWidth="2"/>
  </svg>
</button>

<button
  onClick={() => {
  setZoom(1)
  setPan({ x: 0, y: 0 })
}}
  onMouseEnter={() => setHoverBtn('zoomReset')}
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() => setPressedBtn('zoomReset')}
  onMouseUp={() => setPressedBtn(null)}
  style={modalPrimaryButtonStyle(pressedBtn, 'zoomReset', hoverBtn === 'zoomReset')}
>
  <img
    src={resetIcon}
    alt="reset"
    width={24}
    height={24}
    style={{ pointerEvents: 'none' }}
  />
</button>

</div>
        <h3 style={{ fontSize: 24, margin: 0 }}>{t('YOUR_NETWORK')}</h3>

        {!user ? (
          <div>{t('LOADING')}</div>
        ) : (
          <>
<div
  style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(8, auto)',
    rowGap: 4,
    columnGap: 6,
    alignItems: 'center',
    width: 'fit-content'
  }}
>
  <div>{t('LEVEL1', { level: 1 })} {t('MEMBERS')}:</div>
  <div>
    <strong>{levelMap[1].length}</strong> 
  </div>
  <div style={{ padding: '0 6px' }}>·</div>
  <div>{t('stats.cashback')}:</div>
  <strong style={{ textAlign: 'right', minWidth: 70 }}>
    {formatCurrency(cbL1)}
  </strong>
  <div style={{ padding: '0 6px' }}>·</div>
  <div>{t('REFERRAL_BONUS')} {ymNow()}:</div>
  <strong style={{ textAlign: 'right', minWidth: 70 }}>
    {formatCurrency(refL1)}
  </strong>

  <div>{t('LEVEL2', { level: 2 })} {t('MEMBERS')}:</div>
  <div>
    <strong>{levelMap[2].length}</strong>
  </div>
  <div style={{ padding: '0 6px' }}>·</div>
  <div>{t('stats.cashback')}:</div>
  <strong style={{ textAlign: 'right', minWidth: 70 }}>
    {formatCurrency(cbL2)}
  </strong>
  <div style={{ padding: '0 6px' }}>·</div>
  <div>{t('REFERRAL_BONUS')} {ymNow()}:</div>
  <strong style={{ textAlign: 'right', minWidth: 70 }}>
    {formatCurrency(refL2)}
  </strong>

  <div>{t('LEVEL3', { level: 3 })} {t('MEMBERS')}:</div>
  <div>
    <strong>{levelMap[3].length}</strong>
  </div>
  <div style={{ padding: '0 6px' }}>·</div>
  <div>{t('stats.cashback')}:</div>
  <strong style={{ textAlign: 'right', minWidth: 70 }}>
    {formatCurrency(cbL3)}
  </strong>
  <div style={{ padding: '0 6px' }}>·</div>
  <div>{t('REFERRAL_BONUS')} {ymNow()}:</div>
  <strong style={{ textAlign: 'right', minWidth: 70 }}>
    {formatCurrency(refL3)}
  </strong>
</div>

		<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
		  <div>
 		   {t('NETWORK_TOTAL_MEMBERS')}: <strong>{networkData.length}</strong>
 		 </div>

 		 {/* teplomer */}
		<div
		onMouseEnter={(e) => {
 		 const rect = e.currentTarget.getBoundingClientRect()

 		 setHoveredNode({
 		   x: rect.left + rect.width / 2,
 		   y: rect.top,
 		   type: 'networkRemaining'
 		 })
		}}
  		onMouseLeave={() => setHoveredNode(null)}
  		style={{
 		   display: 'flex',
  		  alignItems: 'center',
  		  gap: 8
 		 }}
		>    <div
   		   style={{
    		    width: 120,
     		   height: 10,
     		   background: '#e5e5e5',
     		   borderRadius: 20,
     		   overflow: 'hidden'
    		  }}
   		 >
 		     <div
  		      style={{
    		      width: `${progressPercent}%`,
    		      height: '100%',
    		      background: '#107c6b'
   		     }}
   		   />
   		 </div>

   		 <div>
   		     {networkData.length} / 155
  		  </div>
 		 </div>
		</div>

            <div style={{ margin: '12px 0' }}>
              {[
                { color: 'red', key: 'LEGEND.RED' },
                { color: 'gray', key: 'LEGEND.GRAY' },
                { color: 'green', key: 'LEGEND.GREEN' }
              ].map(({ color, key }) => (
                <div
                  key={key}
                  style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                >
                  <svg width={radius * 2} height={radius * 2} style={{ marginRight: 8 }}>
                    <g transform={`translate(${radius},${radius})`}>
                      <circle r={radius} fill={color} stroke="#fff" strokeWidth={2} />
                      {renderFace(faceByColor[color] || 'awkward')}
                    </g>
                  </svg>
                  <span>{t(key)}</span>
                </div>
              ))}
            </div>
		<div
 		 style={{
 		   fontSize: 14,
 		   color: '#666',
  		  marginTop: -2,
  		  marginBottom: 2
 		 }}
		>
		  {t('HOVER_NODES_DETAIL')}
		</div>
<div
  style={{
    overflow: 'hidden',
    flex: 1,
    minHeight: 0,
    width: '100%',
    cursor: isDragging ? 'grabbing' : 'grab'
  }}
  onMouseDown={(e) => {
    setIsDragging(true)
    setDragStart({
      x: e.clientX - pan.x,
      y: e.clientY - pan.y
    })
  }}
  onMouseMove={(e) => {
    if (!isDragging) return
    setPan({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }}
  onMouseUp={() => setIsDragging(false)}
  onMouseLeave={() => setIsDragging(false)}
  onWheel={(e) => {
    e.preventDefault()

    setZoom(z => {
      const delta = e.deltaY > 0 ? -0.1 : 0.1
      const next = z + delta
      if (next < 0.4) return 0.4
      if (next > 3) return 3
      return Number(next.toFixed(2))
    })
  }}
>
  <svg
    width="99%"
    height={svgHeight}
    viewBox={`0 0 ${svgWidth / zoom} ${svgHeight / zoom}`}
    preserveAspectRatio="xMidYMin meet"
    style={{
      background: '#fafafa',
      border: '1px solid #ddd',
      borderRadius: 8
    }}
  >
<g transform={`translate(${pan.x}, ${pan.y})`}>
                {networkData.map((l, i) => {
                  const p = coords[l.parent_id]
                  const c = coords[l.child_id]
                  if (!p || !c) return null
                  return (
                    <line
                      key={i}
                      x1={p.x}
                      y1={p.y}
                      x2={c.x}
                      y2={c.y}
                      stroke="#107c6b"
                      strokeWidth={2}
                    />
                  )
                })}

                {Object.entries(coords).map(([idStr, { x, y }]) => {
                  const id = Number(idStr)
                  const color = nodeColors[id] || 'red'
                  const faceType = faceByColor[color] || 'awkward'
                  const isSelected = id === selectedNodeId
                  const isHovered = id === hoveredNodeId
                  const hasReferralBonus = (refBonusByChild[id] || 0) > 0

                  return (
                    <g
                      key={id}
                      transform={`translate(${x},${y})`}
                      style={{ cursor: 'default' }}
                      onMouseEnter={(e) => {
                        setHoveredNodeId(id)
                        setHoveredNode({ x: e.clientX, y: e.clientY, color, id })
                      }}
                      onMouseLeave={() => {
                        setHoveredNodeId(null)
                        setHoveredNode(null)
                      }}
                    >
                      <circle
                        r={radius}
                        fill={color}
                        stroke={isSelected || isHovered ? '#000' : '#fff'}
                        strokeWidth={isSelected || isHovered ? 3 : 2}
                      />

                      {renderNodeContent({ isHovered, id, faceType })}

                      {hasReferralBonus && (
                        <text
                          x={radius + 1}
                          y={-radius + 1}
                          fontSize="14"
                          fontWeight="bold"
                          fill="#000"
                          textAnchor="start"
                          dominantBaseline="hanging"
                          style={{ pointerEvents: 'none' }}
                        >
                          B
                        </text>
                      )}
                    </g>
                  )
                })}
		</g>
              </svg>
            </div>

            {hoveredNode && (
              <div
		style={{
 		 position: 'fixed',
 		 top:
 	   hoveredNode.type === 'networkRemaining'
 	     ? hoveredNode.y - 35
  		    : hoveredNode.y + 10,
  		left:
  		  hoveredNode.type === 'networkRemaining'
  		    ? hoveredNode.x
  		    : hoveredNode.x + 10,
 		 transform:
 		   hoveredNode.type === 'networkRemaining'
  		    ? 'translateX(-50%)'
  		    : 'none',
 		 background: '#fff',
 		 padding: '6px 10px',
 		 border: '1px solid #107c6b',
 		 borderRadius: 4,
		  whiteSpace: 'pre-line',
		  pointerEvents: 'none',
 		 fontSize: 15,
  		zIndex: 99999
		}}
              >
                {(() => {

		if (hoveredNode.type === 'networkRemaining') {
		  return (
 		   <>
  		    {t('NETWORK_REMAINING_PREFIX')}{" "}
  		    <strong>{remaining}</strong>{" "}
  		    {t('NETWORK_REMAINING_SUFFIX')}
		    </>
		  )
		}
                  const color = hoveredNode.color

                  if (color !== 'green') {
                    return color === 'red' ? t('NO_ACTIVATION') : t('NO_ACTIVITY')
                  }

                  const id = hoveredNode.id

if (id === user.id) {
  return (
    <>
      <div>
        {t('CB_WILL_BE_PAID')}: <strong>{formatCurrency(cbBase)}</strong>
      </div>

      {refBonusTotal > 0 && (
        <div>
          {t('REF_WILL_BE_PAID')}: <strong>{formatCurrency(refBonusTotal)}</strong>
        </div>
      )}

      <div>
        {t('TOTAL')}: <strong>{formatCurrency(cbTotal + refBonusTotal)}</strong>
      </div>

      <div style={{ marginTop: 6 }}>
        <div>
          <strong>
            {t('CASHBACK')} {ymNow()}
          </strong>
        </div>

        <div>
          {t('BASE')}: <strong>{formatCurrency(cbBase)}</strong>
        </div>

        <div>
          L1: <strong>{formatCurrency(cbL1)}</strong>
        </div>

        <div>
          L2: <strong>{formatCurrency(cbL2)}</strong>
        </div>

        <div>
          L3: <strong>{formatCurrency(cbL3)}</strong>
        </div>

        <div>
          {t('TOTAL')}: <strong>{formatCurrency(cbTotal)}</strong>
        </div>
      </div>

      <div style={{ marginTop: 6 }}>
        <div>
          <strong>
            {t('REFERRAL_BONUS')} {ymNow()}
          </strong>
        </div>

        <div>
          L1: <strong>{formatCurrency(refL1)}</strong>
        </div>

        <div>
          L2: <strong>{formatCurrency(refL2)}</strong>
        </div>

        <div>
          L3: <strong>{formatCurrency(refL3)}</strong>
        </div>

        <div>
          {t('TOTAL')}: <strong>{formatCurrency(refBonusTotal)}</strong>
        </div>
      </div>
    </>
  )
}

                  const amount = cbByNode[id]?.amount || 0
                  const refForThisChild = refBonusByChild[id] || 0

                  return (
                    <>
                      <div>
                        {t('CB_WILL_BE_PAID')}: <strong>{formatCurrency(amount)}</strong>
                      </div>
                      {refForThisChild > 0 && (
                        <div>
                          {t('REF_FOR_MEMBER')}: <strong>{formatCurrency(refForThisChild)}</strong>
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            )}

<div
  style={{
    marginTop: modalSize === 'max' ? 20 : 'auto',
    marginBottom: modalSize === 'max' ? 20 : -15,
    display: 'flex',
    justifyContent: 'center'
  }}
>
              <button
                onClick={onClose}
                onMouseEnter={() => setHoverBtn('close')}
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() => setPressedBtn('close')}
                onMouseUp={() => setPressedBtn(null)}
                style={modalSecondaryButtonStyle(pressedBtn, 'close', hoverBtn === 'close')}
              >
                {t('CLOSE')}
              </button>
            </div>
          </>
        )}
      </div>
    </ModalLayout>
  )
}